/**
 * Call session (batch 6b) — Signal-style voice calls.
 *
 * This module owns the single active LiveKit session for an outbound or
 * inbound call. It exposes the lifecycle the UI components hook into:
 *
 *   - `startOutbound(number, displayName)` — dialer pressed Dial.
 *   - `acceptIncoming(callId, displayName)` — owner tapped Accept.
 *   - `decline(callId)` — owner tapped Decline on the ring overlay.
 *   - `hangup()` — owner tapped End on the active call.
 *   - `toggleMute()`, `toggleSpeaker()` — in-call controls.
 *   - `getState()` — current session snapshot (or null).
 *
 * The session subscribes to the server's `<serverId>:calls` SSE topic so
 * incoming rings from the webhook land here without the dialer being open.
 *
 * LiveKit client is the same package voice channels use. SIM7600 audio is
 * 8 kHz mono PCM — LiveKit handles the Opus transcode on its own edge, we
 * just publish whatever the bridge participant sends.
 */

import { post, get } from '../api';
import { state } from '../state';
import { realtime, channelTopic, type CallRow, type RealtimeEvent } from '../realtime';
import { showToast } from './toast';
import { Room, RoomEvent, Track, createLocalAudioTrack } from 'livekit-client';
import type {
  LocalAudioTrack,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
} from 'livekit-client';

interface DialResp {
  call: CallRow;
  livekit_url: string;
  owner_token: string;
  room: string;
}
interface AnswerResp {
  call: CallRow;
  livekit_url: string;
  owner_token: string;
  room: string;
}

export interface CallUiState {
  call: CallRow;
  phase: 'ringing-out' | 'ringing-in' | 'active' | 'ended';
  connected: boolean;
  muted: boolean;
  speakerOn: boolean;
  minimised: boolean;
  startedAt: number;       // monotonic ms, used for the 1 Hz timer label
  answeredAt: number | null;
  error: string | null;
  dtmfDigits?: string;     // digits pressed on the in-call keypad this call
}

type Listener = (s: CallUiState | null) => void;

let room: Room | null = null;
let localAudio: LocalAudioTrack | null = null;
const remoteAudioEls = new Map<string, HTMLAudioElement>();
let ui: CallUiState | null = null;
let listeners = new Set<Listener>();
let topicSubscribed: string | null = null;
let unsubRealtime: (() => void) | null = null;

/** Subscribe to UI changes. Called by every call-related component. */
export function onCallChange(fn: Listener): () => void {
  listeners.add(fn);
  fn(ui);
  return () => { listeners.delete(fn); };
}

export function getCallState(): CallUiState | null {
  return ui;
}

function emit(): void {
  for (const fn of listeners) {
    try { fn(ui); } catch (e) { console.error('call listener error', e); }
  }
}

/** Ensure the SSE topic for the current server is subscribed. Safe to call
 *  repeatedly — only rebuilds when the server changes. */
export function ensureCallSubscription(): void {
  const sid = state.currentServerId ?? state.currentServer;
  if (!sid) return;
  const topic = channelTopic(sid, 'calls');
  if (topicSubscribed === topic) return;
  if (topicSubscribed) realtime.unsubscribeChannel(topicSubscribed);
  topicSubscribed = topic;
  realtime.connect();
  realtime.subscribeChannel(topic);
  if (!unsubRealtime) {
    unsubRealtime = realtime.on((ev: RealtimeEvent) => { handleRealtime(ev); });
  }
}

function handleRealtime(ev: RealtimeEvent): void {
  if (ev.type === 'call_incoming' && ev.call.status === 'ringing' && ev.call.direction === 'in') {
    // Only show one ring at a time; ignore if we're already in a call.
    if (ui) return;
    ui = {
      call: ev.call,
      phase: 'ringing-in',
      connected: false,
      muted: false,
      speakerOn: true,
      minimised: false,
      startedAt: Date.now(),
      answeredAt: null,
      error: null,
    };
    emit();
    playRingtone();
    // Browser popup when tab is hidden — pulls user's attention back.
    const who = ev.call.counterparty_name || ev.call.counterparty_phone || 'Someone';
    void import('./notifications').then(mod => {
      mod.notify({
        kind: 'call',
        title: 'Incoming call',
        body: `Incoming: ${who}`,
        onClick: (): void => {
          // Focus happens in notify(); nothing else to do — the call overlay
          // is already mounted and will be visible after window.focus().
        },
      });
    });
    return;
  }
  if (ev.type === 'call_answered' && ui && ui.call.id === ev.call.id) {
    ui = { ...ui, call: ev.call, phase: 'active', answeredAt: Date.now() };
    stopRingtone();
    emit();
    return;
  }
  if (ev.type === 'call_ended' && ui && ui.call.id === ev.call.id) {
    ui = { ...ui, call: ev.call, phase: 'ended' };
    stopRingtone();
    emit();
    void teardown();
    return;
  }
  if (ev.type === 'call_updated' && ui && ui.call.id === ev.call.id) {
    ui = { ...ui, call: ev.call };
    emit();
  }
}

/** POST /api/calls/dial and join the LiveKit room. */
export async function startOutbound(number: string, displayName?: string): Promise<void> {
  if (ui) {
    // A finished/torn-down session can linger if its end event was missed
    // (webhook hiccup, tab asleep). Only a genuinely live session — overlay
    // still up with a connected room — should block a fresh call.
    if (ui.phase === 'ended' || !room) {
      ui = null;
    } else {
      showToast('Already in a call', 'warn');
      return;
    }
  }
  const sid = state.currentServerId ?? state.currentServer;
  if (!sid) { showToast('Select a server first', 'warn'); return; }
  // Prime autoplay inside the click handler — the LiveKit TrackSubscribed
  // callback fires long after this gesture frame has ended, so we warm up
  // the session's audio-playback permission here while we still can.
  primeAudioAutoplay();
  const personId = state.ownerId || undefined;
  let resp: DialResp;
  try {
    resp = await post<DialResp>('/api/calls/dial', {
      server_id: sid,
      number,
      person_id: personId,
      display_name: displayName,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes('calls_read_only')) {
      showToast('Calls are disabled on this device', 'warn');
    } else if (msg.toLowerCase().includes('voice not configured')) {
      showToast('Voice not configured', 'warn');
    } else {
      showToast(`Dial failed: ${msg}`, 'err');
    }
    return;
  }
  ui = {
    call: resp.call,
    phase: 'ringing-out',
    connected: false,
    muted: false,
    speakerOn: true,
    minimised: false,
    startedAt: Date.now(),
    answeredAt: null,
    error: null,
  };
  emit();
  // Caller-side ringback so dialling has audible feedback until the callee's
  // audio arrives (TrackSubscribed) or the call ends.
  playRingtone();
  await joinRoom(resp.livekit_url, resp.owner_token);
}

/** Owner tapped Accept on an inbound ring. */
export async function acceptIncoming(callId: string, displayName?: string): Promise<void> {
  if (!ui || ui.call.id !== callId) return;
  primeAudioAutoplay();
  let resp: AnswerResp;
  try {
    resp = await post<AnswerResp>('/api/calls/answer', {
      call_id: callId,
      person_id: state.ownerId || undefined,
      display_name: displayName,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    showToast(`Answer failed: ${msg}`, 'err');
    return;
  }
  stopRingtone();
  ui = {
    ...ui,
    call: resp.call,
    phase: 'active',
    answeredAt: Date.now(),
  };
  emit();
  await joinRoom(resp.livekit_url, resp.owner_token);
}

/** Owner tapped Decline on an inbound ring. */
export async function decline(callId: string): Promise<void> {
  if (!ui || ui.call.id !== callId) return;
  stopRingtone();
  try {
    await post('/api/calls/hangup', { call_id: callId, person_id: state.ownerId || undefined });
  } catch { /* swallow — UI already gone */ }
  ui = null;
  emit();
  await teardown();
}

/** Owner tapped End on active call. */
export async function hangup(): Promise<void> {
  if (!ui) return;
  const id = ui.call.id;
  stopRingtone();
  try {
    await post('/api/calls/hangup', { call_id: id, person_id: state.ownerId || undefined });
  } catch { /* swallow */ }
  // Let the SSE `call_ended` broadcast drive the UI; but if the server's
  // already flipped us, mirror that locally so the UI doesn't hang.
  if (ui) ui = { ...ui, phase: 'ended' };
  emit();
  await teardown();
  ui = null;
  emit();
}

export async function toggleMute(): Promise<void> {
  if (!ui || !room) return;
  const next = !ui.muted;
  try {
    await room.localParticipant.setMicrophoneEnabled(!next);
  } catch (e) {
    showToast(`Mic: ${e instanceof Error ? e.message : String(e)}`, 'err');
    return;
  }
  ui = { ...ui, muted: next };
  emit();
}

/** Speaker toggle — in browser-land we can't switch output device reliably,
 *  so speaker off = mute all remote audio tags (so the user can still see the
 *  UI and be on the call silently). This mirrors Signal's "mute call" gesture. */
export function toggleSpeaker(): void {
  if (!ui) return;
  const next = !ui.speakerOn;
  for (const el of remoteAudioEls.values()) el.muted = !next;
  ui = { ...ui, speakerOn: next };
  emit();
}

export function toggleMinimise(): void {
  if (!ui) return;
  ui = { ...ui, minimised: !ui.minimised };
  emit();
}

/** In-call DTMF. Every press gives instant LOCAL feedback (tone + on-screen
 *  digit) and is queued for ordered delivery to the carrier. A single
 *  in-flight pump coalesces rapid presses into one multi-digit request, so the
 *  edge plays them in order with proper inter-digit spacing — mashing the
 *  keypad never drops or merges tones (real-phone behaviour). */
let dtmfQueue = '';
let dtmfSending = false;

export async function sendDtmf(digit: string): Promise<void> {
  if (!/^[0-9*#]$/.test(digit)) return;
  // Instant local tone — never lost, even on rapid mashing.
  playDtmfTone(digit);
  // Reflect the pressed digit in the in-call UI.
  if (ui) {
    ui = { ...ui, dtmfDigits: (ui.dtmfDigits ?? '') + digit };
    emit();
  }
  // Queue for ordered, coalesced delivery to the carrier.
  dtmfQueue += digit;
  void pumpDtmf();
}

/** Drain the DTMF queue with at most ONE request in flight. Each pass sends
 *  every digit queued so far as a single { digits } batch (mirrored to the
 *  legacy `key` field for backward compat), so presses reach the carrier in
 *  order and the edge paces them (hub-people → hub-cell → edge AT+VTS). IVR
 *  menus ("press 1 for…") and PIN entry both work. Best-effort: the local
 *  tones already played, so a network blip never blocks the keypad. */
async function pumpDtmf(): Promise<void> {
  if (dtmfSending) return;
  dtmfSending = true;
  try {
    while (dtmfQueue.length > 0) {
      const batch = dtmfQueue;
      dtmfQueue = '';
      const callId = ui?.call.id ?? '';
      try {
        await post('/api/calls/dtmf', { call_id: callId, digits: batch, key: batch });
      } catch { /* ignore — local feedback already given */ }
    }
  } finally {
    dtmfSending = false;
  }
}

// ── LiveKit connection ───────────────────────────────────────────────────

async function joinRoom(url: string, token: string): Promise<void> {
  const r = new Room({ adaptiveStream: true, dynacast: true });
  room = r;
  r.on(RoomEvent.TrackSubscribed,
    (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (track.kind !== Track.Kind.Audio) return;
      // Remote audio = the call connected. Stop ringback but DO NOT flip phase to 'active'
      // — that signal must come from the call_answered SSE event (the authoritative
      // "answered" signal driven by the modem URC VOICE CALL: BEGIN webhook).
      stopRingtone();
      if (ui && ui.phase !== 'ended') {
        ui = { ...ui, connected: true };
        emit();
      }
      const el = track.attach() as HTMLAudioElement;
      el.setAttribute('data-call-identity', participant.identity);
      el.autoplay = true;
      // Muting BEFORE play() can cause some browsers (notably Safari) to refuse
      // to start the track at all — we defer mute until after play() resolves,
      // then honour the current speakerOn state.
      document.body.appendChild(el);
      remoteAudioEls.set(participant.identity, el);

      const applySpeakerMute = (): void => {
        if (ui) el.muted = !ui.speakerOn;
      };

      el.play()
        .then(() => {
          applySpeakerMute();
          console.info(`[call] subscribed audio track from ${participant.identity}, play() resolved`);
        })
        .catch((err: unknown) => {
          // Autoplay blocked — mic-gesture scope expired before this callback
          // fired. Show a one-tap unblock button; set mute once play succeeds.
          console.warn('[call] remote audio autoplay blocked, awaiting user gesture', err);
          showAutoplayUnblockPrompt(() => {
            el.play().then(() => {
              applySpeakerMute();
              console.info(`[call] audio unblocked after user gesture from ${participant.identity}`);
            }).catch((e: unknown) => {
              console.error('[call] audio unblock retry failed', e);
            });
          });
        });
    });
  r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
    track.detach().forEach(e => e.remove());
    remoteAudioEls.delete(participant.identity);
  });
  r.on(RoomEvent.Disconnected, () => {
    if (!ui) return;
    if (ui.phase !== 'ended') {
      ui = { ...ui, connected: false };
      emit();
    }
  });
  // The cell-edge (remote leg) leaving the room means the carrier call ended.
  // The call-event webhook is the authoritative signal but is unreliable, so
  // end the call here too — otherwise the overlay gets stuck "on call" and
  // blocks the next dial.
  r.on(RoomEvent.ParticipantDisconnected, () => {
    if (ui && ui.phase !== 'ended') {
      ui = { ...ui, phase: 'ended' };
      stopRingtone();
      emit();
      void teardown();
    }
  });
  try {
    await r.connect(url, token);
    if (ui) { ui = { ...ui, connected: true }; emit(); }
  } catch (e) {
    showToast(`Call connect failed: ${e instanceof Error ? e.message : String(e)}`, 'err');
    if (ui) { ui = { ...ui, error: 'connect', phase: 'ended' }; emit(); }
    await teardown();
    return;
  }
  try {
    const track = await createLocalAudioTrack({ echoCancellation: true, noiseSuppression: true, autoGainControl: true });
    localAudio = track;
    await r.localParticipant.publishTrack(track);
  } catch (e) {
    showToast(`Microphone unavailable: ${e instanceof Error ? e.message : String(e)}`, 'warn');
  }
}

async function teardown(): Promise<void> {
  stopRingtone();
  dismissAutoplayUnblockPrompt();
  for (const el of remoteAudioEls.values()) el.remove();
  remoteAudioEls.clear();
  if (localAudio) {
    try { localAudio.stop(); } catch { /* ignore */ }
    localAudio = null;
  }
  const r = room;
  room = null;
  if (r) {
    try { await r.disconnect(); } catch { /* ignore */ }
  }
}

// ── Ringtone / DTMF ──────────────────────────────────────────────────────

let ringAudioCtx: AudioContext | null = null;
let ringOsc: OscillatorNode | null = null;
let ringTimer: ReturnType<typeof setInterval> | null = null;

function playRingtone(): void {
  if (ringAudioCtx) return;
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    ringAudioCtx = ctx;
    const play = (): void => {
      if (!ringAudioCtx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.value = 0.08;
      osc.connect(gain).connect(ctx.destination);
      ringOsc = osc;
      osc.start();
      setTimeout(() => { try { osc.stop(); } catch { /* ignore */ } }, 700);
    };
    play();
    ringTimer = setInterval(play, 2400);
  } catch { /* audio failed, silent ring is fine */ }
}

function stopRingtone(): void {
  if (ringTimer) { clearInterval(ringTimer); ringTimer = null; }
  if (ringOsc) { try { ringOsc.stop(); } catch { /* ignore */ } ringOsc = null; }
  if (ringAudioCtx) { try { void ringAudioCtx.close(); } catch { /* ignore */ } ringAudioCtx = null; }
}

const DTMF_FREQS: Record<string, [number, number]> = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

// ── Autoplay unblock (Chrome/Safari policy) ─────────────────────────────
//
// Browsers drop the user-gesture context by the time LiveKit's
// TrackSubscribed fires, so the `<audio>` element we attach needs an
// explicit `.play()` which may reject. When it does, we surface a
// single-tap prompt that retries play from a fresh gesture.

let autoplayPromptEl: HTMLButtonElement | null = null;

function showAutoplayUnblockPrompt(onClick: () => void): void {
  if (autoplayPromptEl) {
    // Already showing — replace its handler so the latest track is the one unblocked.
    autoplayPromptEl.onclick = (): void => {
      onClick();
      dismissAutoplayUnblockPrompt();
    };
    return;
  }
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'call-autoplay-unblock';
  btn.setAttribute('role', 'alert');
  btn.setAttribute('aria-live', 'assertive');
  btn.textContent = 'Tap to enable call audio';
  Object.assign(btn.style, {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '10px 18px',
    background: 'var(--accent, #5865f2)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    font: 'inherit',
    fontWeight: '600',
    boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
    zIndex: '99999',
  });
  btn.onclick = (): void => {
    onClick();
    dismissAutoplayUnblockPrompt();
  };
  document.body.appendChild(btn);
  autoplayPromptEl = btn;
}

function dismissAutoplayUnblockPrompt(): void {
  if (!autoplayPromptEl) return;
  autoplayPromptEl.remove();
  autoplayPromptEl = null;
}

/** Prime browser autoplay by playing a near-silent <audio> during the
 *  click handler that initiated the call. Keeps LiveKit's later
 *  `.play()` in the good-state autoplay allowlist for the session. */
function primeAudioAutoplay(): void {
  try {
    const silent = document.createElement('audio');
    // 1-frame silent WAV, base64-encoded. Keeps bundle small and avoids a
    // network fetch that might miss the gesture window.
    silent.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    silent.volume = 0;
    silent.muted = true;
    const p = silent.play();
    if (p && typeof p.then === 'function') {
      p.then(() => { try { silent.pause(); silent.remove(); } catch { /* ignore */ } })
       .catch(() => { try { silent.remove(); } catch { /* ignore */ } });
    }
  } catch { /* best effort — if this throws the call still works */ }
}

function playDtmfTone(digit: string): void {
  const freqs = DTMF_FREQS[digit];
  if (!freqs) return;
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const [fa, fb] = freqs;
    const oa = ctx.createOscillator();
    const ob = ctx.createOscillator();
    const gain = ctx.createGain();
    oa.frequency.value = fa;
    ob.frequency.value = fb;
    gain.gain.value = 0.12;
    oa.connect(gain); ob.connect(gain); gain.connect(ctx.destination);
    oa.start(); ob.start();
    setTimeout(() => {
      try { oa.stop(); ob.stop(); } catch { /* ignore */ }
      void ctx.close();
    }, 120);
  } catch { /* best effort */ }
}

/** Resync from the server — used when `resync` SSE event fires. */
export async function resyncActiveCall(): Promise<void> {
  if (!ui) return;
  try {
    const rows = await get<CallRow[]>(`/api/calls?server_id=${encodeURIComponent(ui.call.server_id)}&limit=1`);
    const latest = rows.find(r => r.id === ui!.call.id);
    if (latest) {
      ui = { ...ui, call: latest };
      if (latest.status === 'ended' || latest.status === 'missed' || latest.status === 'busy' || latest.status === 'no_answer' || latest.status === 'failed') {
        ui = { ...ui, phase: 'ended' };
        emit();
        await teardown();
        ui = null;
      }
      emit();
    }
  } catch { /* ignore */ }
}
